"use client"

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { createClient } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { signIn, signUp, forgotPassword } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Link from "next/link";
import { supabase } from "@/lib/supabaseClient";
import { getUtmsFromStorage } from "@/lib/utmUtils";
import { validateReferralCode, createReferralCode } from "@/lib/referralUtils";

function AuthPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register state
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerEmailConfirm, setRegisterEmailConfirm] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [gender, setGender] = useState<string>("");
  const [referralCode, setReferralCode] = useState<string>("");

  // Check for referral code in URL on component mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      // Show a toast to let user know the referral code was applied
      toast({
        title: "Referral Code Applied",
        description: `Referral code ${refCode.toUpperCase()} has been applied to your registration.`,
        duration: 4000,
      });
    }
  }, [searchParams, toast]);

  // Create a supabase client instance.
  const supabaseClient = supabase;

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const { user, error } = await signIn(loginEmail, loginPassword);
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success!",
          description: "You have successfully signed in.",
        });
        router.push("/dashboard");
      }
    } catch (error) {
      // Error handling.
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      toast({
        title: "Error",
        description: "Please enter your email address to reset your password.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await forgotPassword(loginEmail);
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Reset Email Sent",
          description: "Please check your inbox for further instructions.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (registerEmail !== registerEmailConfirm) {
      toast({
        title: "Error",
        description: "Email addresses do not match.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    if (registerPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    if (!gender) {
      toast({
        title: "Error",
        description: "Please select a gender.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Validate referral code if provided
    let referrerUserId = null;
    if (referralCode.trim()) {
      const validation = await validateReferralCode(referralCode.trim());
      if (!validation.valid) {
        toast({
          title: "Invalid Referral Code",
          description: validation.message,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      if (validation.referrer) {
        referrerUserId = validation.referrer.uid;
      }
    }
    
    // First, check if the email is already in the custom users table.
    const { data: existingUser, error: selectError } = await supabaseClient
      .from("users")
      .select("uid")
      .eq("email", registerEmail)
      .maybeSingle();
      
    if (selectError) {
      toast({
        title: "Error",
        description: selectError.message,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    if (existingUser) {
      toast({
        title: "Error",
        description: "This email is already registered.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    const utms = getUtmsFromStorage();

    try {
      const { user, error } = await signUp(registerEmail, registerPassword, gender);
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else if (user) {
        toast({
          title: "Success!",
          description: "Your account has been created.",
        });
        
        // Create referral code for the new user
        const newUserReferralCode = await createReferralCode(user.id);
        
        // Upsert the user into the custom users table with the UID from authentication.
        const { error: dbError } = await supabaseClient
          .from("users")
          .upsert({
            uid: user.id,
            email: registerEmail,
            subscribed: false,
            utm_source: utms.utm_source || null,
            utm_medium: utms.utm_medium || null,
            utm_campaign: utms.utm_campaign || null,
            utm_term: utms.utm_term || null,
            utm_content: utms.utm_content || null,
            referral_code: newUserReferralCode,
            referred_by: referrerUserId,
            referral_count: 0,
            free_account_earned: false,
          });
        if (dbError) {
          toast({
            title: "Database Error",
            description: dbError.message,
            variant: "destructive",
          });
        }
        // Save uid for later (e.g. in localStorage)
        localStorage.setItem("uid", user.id);
        
        // Show referral success message if applicable
        if (referrerUserId) {
          toast({
            title: "Referral Applied!",
            description: "You've been successfully referred. Your referrer will be rewarded when you subscribe.",
            duration: 4000,
          });
        }
        
        // Redirect to a verification page after signing up
        router.push("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="w-full max-w-md space-y-8">
        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <Card>
              <CardHeader>
                <CardTitle>Welcome back</CardTitle>
                <CardDescription>
                  Enter your email to sign in to your account
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleLogin}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input 
                      id="login-email" 
                      type="email" 
                      required 
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input 
                      id="login-password" 
                      type="password" 
                      required 
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col gap-2">
                  <Button className="w-full" type="submit" disabled={isLoading}>
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                  <Button 
                    variant="link" 
                    type="button" 
                    onClick={() => handleForgotPassword()}
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
          <TabsContent value="register">
            <Card>
              <CardHeader>
                <CardTitle>Create an account</CardTitle>
                <CardDescription>
                  Enter your email below to create your account
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleRegister}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input 
                      id="register-email" 
                      type="email" 
                      required 
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email-confirm">Confirm Email</Label>
                    <Input 
                      id="register-email-confirm" 
                      type="email" 
                      required 
                      value={registerEmailConfirm}
                      onChange={(e) => setRegisterEmailConfirm(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input 
                      id="register-password" 
                      type="password" 
                      required 
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">Confirm Password</Label>
                    <Input 
                      id="confirm" 
                      type="password" 
                      required 
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select 
                      value={gender} 
                      onValueChange={setGender}
                      required
                    >
                      <SelectTrigger id="gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referral-code">Referral Code (Optional)</Label>
                    <Input 
                      id="referral-code" 
                      type="text" 
                      placeholder="Enter referral code"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      maxLength={8}
                    />
                    <p className="text-xs text-muted-foreground">
                      Have a referral code? Enter it here to help your friend earn rewards!
                    </p>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button className="w-full" type="submit" disabled={isLoading}>
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </Button>
                </CardFooter>
                <p className="text-sm text-muted-foreground text-center mt-0 pl-5 pr-5 pb-5">
                  By clicking create account, you agree to our{" "}
                  <Link href="/legal" className="hover:underline underline-offset-2">
                    Terms of Service and Privacy Policy
                  </Link>.
                </p>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div> 
    </div>
  );
}

export default function AuthPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    }>
      <AuthPageContent />
    </Suspense>
  );
}